<template>
    <div
        :class="{
            'list-group': true,
            [`list-group--${variant}`]: variant,
            'list-group--non-interactive': !interactive,
        }"
    >
        <div class="list-group__content">
            <div v-if="$slots.header" class="list-group__header">
                <!-- @slot Header content -->
                <slot name="header"></slot>
            </div>
            <div v-if="hasItems" class="list-group__body">
                <ul class="list-group__items">
                    <li
                        v-for="(item, i) in itemList"
                        :key="new Date().getTime() + i"
                        :class="{
                            'list-group__item': true,
                            'list-group__item--disabled': item.disabled,
                        }"
                    >
                        <div v-if="isTrallingCheckbox" class="checkbox">
                            <input
                                :id="`list-group__checkbox_${i}-${item.name}`"
                                v-model="item.checked"
                                type="checkbox"
                                :name="item.name"
                                :value="item.value"
                                class="checkbox__control"
                                :checked="item.checked"
                                :disabled="item.disabled || !interactive"
                                autocomplete="off"
                                @change="onCheckboxChange(item, i, $event)"
                            >
                            <label
                                class="checkbox__label checkbox__label--reverse"
                                :for="`list-group__checkbox_${i}-${item.name}`"
                            >
                                <div class="checkbox__label-text">
                                    <icon
                                        v-if="item.iconName"
                                        :name="item.iconName"
                                        class="m-right--s"
                                        color="grey-600"
                                        size="medium"
                                    />
                                    <div>
                                        <span class="ts--body-small color--grey-900">{{ item.label }}</span>
                                        <p v-if="item.description" class="color--grey-700 ts--caption m-top--xs">
                                            <span v-html="item.description"></span>
                                        </p>
                                    </div>
                                </div>
                            </label>
                        </div>
                        <div v-else class="list-group__item-text">
                            <div class="list-group__item-name">
                                {{ item.label }}
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
            <div v-if="$slots.footer" class="list-group__footer">
                <!-- @slot Footer content -->
                <slot name="footer"></slot>
            </div>
        </div>
    </div>
</template>

<script>
const Types = Object.freeze({ TRAILING_CHECKBOX: 'trailing-checkbox' });

/**
 * List group component
 *
 * @version 4.0.0
 * @example ./examples/ListGroup.md
 */
export default {
    name: 'ListGroup',
    components: {
        Icon: () => ({
            component: import(/* webpackChunkName: "Icon-vue" */ '@v4/common/components/atoms/Icon.vue'),
        }),
    },
    props: {
        /**
         * List group variant. Available variants: `trailing-checkbox`
         */
        variant: {
            type: String,
            default: '',
            validator: (value) => [...Object.values(Types), ''].includes(value),
        },
        /**
         * Items to loop through.
         * When `v-model` and `items` props are both used, will be used
         * `v-model` as item list.
         */
        value: {
            type: Array,
            default: () => [],
        },
        /**
         * Items to loop through when not using `v-model`
         */
        items: {
            type: Array,
            default: () => [],
        },
        /**
         * Whether or not the list is interactive
         */
        interactive: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        hasValue() {
            return !!this.value.length;
        },
        hasItems() {
            return !!this.itemList.length;
        },
        isTrallingCheckbox() {
            return this.variant === Types.TRAILING_CHECKBOX;
        },
        itemList() {
            if (this.hasValue) {
                return this.value;
            }

            return this.items;
        },
    },
    methods: {
        onCheckboxChange(item, index, event) {
            if (this.hasValue) {
                /**
                 * Fired on checkbox change when using `v-model`.
                 * Mainly fired for updating the `v-model` value
                 *
                 * @event input
                 * @type {array}
                 */
                this.$emit('input', this.value);
            }

            /**
             * Fired on checkbox change
             *
             * @event change
             * @type {object}
             */
            this.$emit('change', { item, index, event });
        },
    },
};
</script>
